import {
  LoggerType,
  TopBar,
  type LogActionsType,
} from '@fanduel/casino-swj-consumer';
import { getRegion } from '@/utilities/getters/cookieGetters';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  type AppOrientation,
  useAppOrientation,
} from '@/lostHooks/orientation/useOrientation';
import { isAndroidWrapper } from '@/utilities/helpers/wrapperHelpers';

import { useStore } from '@/store/useStore';
import { getBalanceData } from '@/unified-modules/wallet/helpers/get-balance-data';
import { getSession } from '@/unified-modules/account/account-library';
import { GAME_WINDOW_TO_CLIENT_EVENTS } from '@/event-manager/types';
import useEventManagerEmitter from '@/event-manager/use-event-manager-emiter';
import { openURLMobile } from './helpers/openURL';
import { usePopup } from '../Popup/usePopup';
import { PopupConfig, PopupTypes } from '../Popup/types';
import {
  logSwjConsumerRumAction,
  logSwjConsumerRumError,
  reportSwjConsumerErrorPopup,
} from './helpers/swj-consumer-error-reporting';
import { getBaseURL } from '@/utilities/getters/gameContextGetters';
import { JackpotTier } from './types';
import { getGlsBaseUrl } from '@/api/gls/get-gls-base-url';
import { useShallow } from 'zustand/react/shallow';
import './styles/default.scss';
import {
  trackErrorModalMultiplierFailure,
  trackJackpotsModalToggle,
  trackJackpotsModalViewed,
  trackJackpotsOptInToggled,
  trackJackpotWin,
} from './helpers/swjTracking';
const getOrientationOrMaybeDeviceType = (orientation: AppOrientation) => {
  if (orientation === 'portrait') {
    return 'portrait';
  }
  if (isAndroidWrapper()) {
    return 'landscape';
  }
  return 'desktop';
};

export const SWJModule = () => {
  const {
    jackpotData,
    isTopBarVisible,
    setIsTopBarVisible,
    setGameExpansionToggleDisplayState,
    setJackpotOptInStatus,
    isDybFeatureFlagEnabled: isDYBEnabled,
  } = useStore(useShallow((state) => state.swj));

  const userId = useStore((state) => state?.userSessionContext?.userId);
  const { spinInProgress: isSpinInProgress, gameWindowOpen: isMenuOpen } =
    useStore((state) => state?.gameWindowContext);
  const { showTCPopup: shouldShowTCPopup, showDYBPopup: shouldShowDYBPopup } =
    useStore((state) => state?.customerPreferences);
  // Hold the DYB popup until the game has finished loading; the flag lands from
  // /features at mount, so without this it opens over the loading screen.
  const isGameLoaded = useStore((state) => state.gameStatus.gameLoadSuccess);
  const { gameUid, provider } = useStore((state) => state?.gameContext);
  const orientation = useAppOrientation();
  const deviceType = useMemo(
    () => getOrientationOrMaybeDeviceType(orientation),
    [orientation],
  );
  const refreshUUID = useRef<string>(null);
  const { emitClientEvent } = useEventManagerEmitter();
  const reloadBalance = useCallback(
    // TODO: move this into a common helper
    (balance) =>
      emitClientEvent(GAME_WINDOW_TO_CLIENT_EVENTS.RELOAD_BALANCE, balance),
    [emitClientEvent],
  );
  const glsEndpoint = useMemo(() => getGlsBaseUrl(), []);
  const prismicJackpotRulesURL = jackpotData?.rules_page_url?.url;
  const jackpotRulesURL =
    prismicJackpotRulesURL ??
    `${getBaseURL()}/site-wide-jackpot-rules?desiredRegion=${getRegion()}`;
  const jackpotPath = jackpotData?.rules_page?.url;
  const openRulesExternallyAndroid = jackpotData?.open_rules_externally_android;

  const { showPopup, setJackpotWin } = usePopup();

  const handleJackpotRules = useCallback(
    (e) => {
      e.preventDefault();
      if (isAndroidWrapper()) {
        openURLMobile(
          jackpotPath || jackpotRulesURL,
          openRulesExternallyAndroid ?? false,
        ); // mobile
      } else {
        window.open(jackpotPath || jackpotRulesURL, '_blank')?.focus(); // desktop
      }
    },
    [jackpotPath, jackpotRulesURL, openRulesExternallyAndroid],
  );

  const logActions = {
    logOpenedClosedModal: (optedInOption: string, showTCPopup: boolean) => {
      trackJackpotsModalToggle(optedInOption, showTCPopup);
    },
    logJackpotsModalViewed: (optedInOption: string) => {
      trackJackpotsModalViewed(optedInOption);
    },
    logOptInOutToggle: (
      optedInOption: string,
      jackpotTiers: Array<JackpotTier> = [],
      multiplier?: number,
    ) => {
      const isDYB = typeof multiplier === 'number';
      trackJackpotsOptInToggled(optedInOption, multiplier, jackpotTiers, isDYB);
    },
    logDYBModalViewed: (optedInOption: string) => {
      trackJackpotsModalViewed(optedInOption);
    },
    logDYBOpenedClosedModal: (optedInOption: string, showTCPopup: boolean) => {
      trackJackpotsModalToggle(optedInOption, showTCPopup);
    },
    logJackpotWin: (optedIn: number, jackpotTierId: string, amount: number) => {
      const tier = jackpotData?.tiers?.find((t) => t.tier_id === jackpotTierId);
      const tierName = tier?.short_tier_name?.[0]?.text?.toLowerCase() ?? '';
      trackJackpotWin(optedIn, amount, tierName);
    },
    logOptInError: (optedIn: number) => {
      trackErrorModalMultiplierFailure(optedIn);
    },
  };

  const pushBalanceToProvider = useCallback(
    (balance, hash) => {
      if (!isSpinInProgress && refreshUUID.current === hash) {
        reloadBalance(balance);
      }
    },
    [isSpinInProgress, reloadBalance],
  );

  const getAndReloadBalance = useCallback(() => {
    const currentUUID = refreshUUID.current;
    getBalanceData((balance) => {
      if (currentUUID) {
        pushBalanceToProvider(balance, currentUUID);
      }
    });
  }, [getBalanceData, pushBalanceToProvider]);

  useEffect(() => {
    if (isSpinInProgress) {
      refreshUUID.current = crypto.randomUUID();
    }
  }, [isSpinInProgress]);

  if (!jackpotData?.provider?.uid) {
    return null;
  }

  return (
    <TopBar
      origin={'game_window'}
      display={!isMenuOpen}
      jackpotData={jackpotData}
      userId={userId ?? ''}
      isDYBEnabled={isDYBEnabled}
      isSpinInProgress={isSpinInProgress}
      fgpEndpoint={glsEndpoint}
      isTopBarExpanded={isTopBarVisible}
      forceTCPopup={
        isDYBEnabled ? shouldShowDYBPopup && isGameLoaded : shouldShowTCPopup
      }
      gameProvider={provider ?? undefined}
      jackpotRulesURL={jackpotRulesURL}
      deviceType={deviceType}
      logErrorMethod={logSwjConsumerRumError as unknown as LoggerType}
      logActionMethod={logSwjConsumerRumAction as unknown as LoggerType}
      showErrorPopupMethod={(payload: { type: PopupTypes; data }) => {
        reportSwjConsumerErrorPopup(showPopup, payload, gameUid ?? undefined);
      }}
      reloadBalance={reloadBalance}
      getAndReloadBalance={getAndReloadBalance}
      setGameExpansionToggleDisplay={(val) => {
        setGameExpansionToggleDisplayState(val);
      }}
      setSWJOptIn={(val) => {
        setJackpotOptInStatus(val);
      }}
      setGameWindowExpansion={setIsTopBarVisible}
      handleJackpotRules={handleJackpotRules}
      setJackpotWin={(payload) =>
        setJackpotWin(payload as unknown as PopupConfig)
      }
      getSession={getSession as unknown as () => Promise<{ token: string }>}
      logActions={logActions as unknown as LogActionsType}
      brand={process.env.BRAND ?? ''}
      theme={process.env.PRODUCT ?? ''}
      casinoLobbyURL={getBaseURL()}
    />
  );
};
